(() => {
  if (window.__nutritionUpsellTestInitialized) return;
  window.__nutritionUpsellTestInitialized = true;

  const DEFAULT_ITEMS = '43456652116070:1,43456652148838:1,42836121288806:1';
  const DEFAULT_STORE_DOMAIN = 'nutrition-su.myshopify.com';
  const DEFAULT_API_VERSION = '2025-10';
  const DEFAULT_STOREFRONT_TOKEN = '76d349f53add8fb2f0560350acfc089f';
  let preCheckoutRedirectBound = false;

  // 購入オプションが複数ある商品を残す。ビタミンB群のみIDの関係が逆。
  // QR限定商品は統合対象外のため、この対応表には含めない。
  const canonicalVariantMap = {
    '42836103561318': '43456652116070',
    '43456652116070': '43456652116070',
    '42835651788902': '43456652148838',
    '43456652148838': '43456652148838',
    '42836121288806': '42836121288806',
    '43456651591782': '42836121288806',
    '42836113981542': '43456651821158',
    '43456651821158': '43456651821158',
    '42836119093350': '43456651755622',
    '43456651755622': '43456651755622',
    '42836110508134': '43456651853926',
    '43456651853926': '43456651853926'
  };

  const yen = (amount, currencyCode = 'JPY') => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return '価格未取得';
    try {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0
      }).format(value);
    } catch (error) {
      return `${Math.round(value)} ${currencyCode}`;
    }
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[char]));

  const parseItems = (raw) => {
    const source = String(raw || '').trim();
    if (!source) return [];
    return source.split(',').map((chunk) => {
      const [idRaw, qtyRaw] = chunk.split(':');
      const id = String(idRaw || '').trim();
      if (!/^\d+$/.test(id)) return null;
      const quantity = parseInt(String(qtyRaw || '').trim(), 10);
      return {
        id: canonicalVariantMap[id] || id,
        qty: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
      };
    }).filter(Boolean);
  };

  const dedupeItems = (items) => {
    const map = new Map();
    items.forEach((item) => {
      if (!item?.id) return;
      const current = map.get(item.id);
      map.set(item.id, {
        id: item.id,
        qty: (current?.qty || 0) + (item.qty || 1)
      });
    });
    return Array.from(map.values());
  };

  const getConfig = (el) => ({
    storeDomain: el.dataset.storeDomain || DEFAULT_STORE_DOMAIN,
    apiVersion: el.dataset.apiVersion || DEFAULT_API_VERSION,
    token: el.dataset.storefrontToken || DEFAULT_STOREFRONT_TOKEN,
    defaultItems: el.dataset.defaultItems || DEFAULT_ITEMS
  });

  const gid = (id) => {
    const value = `gid://shopify/ProductVariant/${id}`;
    try {
      return btoa(value);
    } catch (error) {
      return value;
    }
  };

  const fallbackProduct = (id) => ({
    id,
    title: `おすすめサプリ ${id}`,
    variantTitle: '',
    price: null,
    currencyCode: 'JPY',
    imageUrl: '',
    imageAlt: 'おすすめサプリ',
    productType: 'ワカサプリ',
    description: '健診データに基づいて追加提案するサプリメントです。'
  });

  const fetchVariants = (variantIds, config) => {
    const ids = Array.from(new Set(variantIds.filter(Boolean)));
    if (!ids.length) return Promise.resolve(new Map());

    const query = `query VariantNodes($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          price { amount currencyCode }
          compareAtPrice { amount currencyCode }
          product {
            title
            productType
            description
            onlineStoreUrl
            featuredImage { url altText }
          }
        }
      }
    }`;

    return fetch(`https://${config.storeDomain}/api/${config.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token
      },
      body: JSON.stringify({
        query,
        variables: { ids: ids.map(gid) }
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Storefront API request failed');
        return res.json();
      })
      .then((payload) => {
        const map = new Map();
        const nodes = payload?.data?.nodes || [];
        nodes.forEach((node) => {
          const match = String(node?.id || '').match(/ProductVariant\/(\d+)/);
          if (!match) return;
          map.set(match[1], {
            id: match[1],
            title: node?.product?.title || '商品名未取得',
            variantTitle: node?.title || '',
            price: node?.price?.amount,
            compareAtPrice: node?.compareAtPrice?.amount,
            currencyCode: node?.price?.currencyCode || 'JPY',
            imageUrl: node?.product?.featuredImage?.url || '',
            imageAlt: node?.product?.featuredImage?.altText || node?.product?.title || 'おすすめサプリ',
            productType: node?.product?.productType || 'ワカサプリ',
            productUrl: node?.product?.onlineStoreUrl || '',
            description: summarize(node?.product?.description)
          });
        });
        ids.forEach((id) => {
          if (!map.has(id)) map.set(id, fallbackProduct(id));
        });
        return map;
      })
      .catch(() => {
        const map = new Map();
        ids.forEach((id) => map.set(id, fallbackProduct(id)));
        return map;
      });
  };

  const summarize = (value) => {
    const text = String(value || '')
      .replace(/■\s*商品説明/g, '')
      .replace(/商品説明/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return '今のあなたに必要な栄養素を補うためのサプリメントです。';
    return text.length > 72 ? `${text.slice(0, 72)}...` : text;
  };

  const displayName = (product) => {
    if (!product) return 'おすすめサプリ';
    return product.variantTitle && product.variantTitle !== 'Default Title'
      ? `${product.title} - ${product.variantTitle}`
      : product.title;
  };

  const itemImage = (product) => product?.imageUrl
    ? `<img src="${escapeHtml(product.imageUrl)}" alt="${escapeHtml(product.imageAlt || displayName(product))}" loading="lazy">`
    : '<span class="upsell-test__image-placeholder" aria-hidden="true"></span>';

  const renderItemList = (items, productMap, className = 'upsell-test__items') => (
    `<ul class="${className}">
      ${items.map((item) => {
        const product = productMap.get(item.id) || fallbackProduct(item.id);
        return `<li class="${className === 'upsell-test__mini-items' ? 'upsell-test__mini-item' : 'upsell-test__item'}">
          ${itemImage(product)}
          <div>
            <p class="${className === 'upsell-test__mini-items' ? 'upsell-test__mini-title' : 'upsell-test__item-title'}">${escapeHtml(displayName(product))}</p>
            <p class="${className === 'upsell-test__mini-items' ? 'upsell-test__mini-meta' : 'upsell-test__item-meta'}">${escapeHtml(product.description)}${item.qty > 1 ? ` / 数量 ${item.qty}` : ''}</p>
          </div>
        </li>`;
      }).join('')}
    </ul>`
  );

  const totalFor = (items, productMap) => {
    let total = 0;
    let currencyCode = 'JPY';
    items.forEach((item) => {
      const product = productMap.get(item.id);
      const price = Number(product?.price);
      if (Number.isFinite(price)) total += price * item.qty;
      if (product?.currencyCode) currencyCode = product.currencyCode;
    });
    return total > 0 ? yen(total, currencyCode) : '価格確認中';
  };

  const cartAddUrl = (items, returnTo = '/cart') => {
    const params = new URLSearchParams();
    dedupeItems(items).forEach((item, index) => {
      params.append(`items[${index}][id]`, item.id);
      params.append(`items[${index}][quantity]`, String(item.qty || 1));
    });
    params.append('return_to', returnTo);
    return `/cart/add?${params.toString()}`;
  };

  const planDefinitions = (items) => {
    const plans = [
      {
        key: 'single',
        title: '1商品だけ購入',
        badge: '現状維持',
        copy: 'まずは最優先の1商品から始める、最もシンプルな導線です。',
        items: items.slice(0, 1)
      },
      {
        key: 'two',
        title: '2商品セットにする',
        badge: '追加提案',
        copy: '不足リスクが高い2商品目を同時に提案し、購入単価を上げるパターンです。',
        items: items.slice(0, 2),
        featured: true
      },
      {
        key: 'three',
        title: '3商品セットにする',
        badge: '最大提案',
        copy: '診断結果に基づく上位3商品をまとめて提示する、最も訴求の強いパターンです。',
        items: items.slice(0, 3)
      }
    ];
    return plans.filter((plan) => plan.items.length > 0);
  };

  const renderOfferCards = (plans, productMap, mode) => plans.map((plan) => {
    const primaryReturnTo = mode === 'precheckout' ? '/checkout' : '/cart';
    const secondaryReturnTo = mode === 'precheckout' ? '/cart' : '/checkout';
    const primaryLabel = mode === 'precheckout'
      ? '追加して決済へ進む'
      : mode === 'thank-you'
        ? 'おすすめをカートへ追加'
        : 'カートへ追加';
    const secondaryLabel = mode === 'precheckout'
      ? '追加してカートに戻る'
      : mode === 'thank-you'
        ? '追加して再度決済へ進む'
        : '追加して決済へ進む';

    return `<article class="upsell-test-card${plan.featured ? ' upsell-test-card--featured' : ''}">
      <div class="upsell-test-card__head">
        <span class="upsell-test-card__badge">${escapeHtml(plan.badge)}</span>
        <h3 class="upsell-test-card__title">${escapeHtml(plan.title)}</h3>
        <p class="upsell-test-card__copy">${escapeHtml(plan.copy)}</p>
      </div>
      <div class="upsell-test-card__body">
        ${renderItemList(plan.items, productMap, 'upsell-test__mini-items')}
        <div class="upsell-test-card__price">${escapeHtml(totalFor(plan.items, productMap))}</div>
        <div class="upsell-test-card__actions">
          <a class="upsell-test__button" href="${cartAddUrl(plan.items, primaryReturnTo)}">${primaryLabel}</a>
          <a class="upsell-test__button-secondary" href="${cartAddUrl(plan.items, secondaryReturnTo)}">${secondaryLabel}</a>
        </div>
      </div>
    </article>`;
  }).join('');

  const getPageItems = (root, config) => {
    const params = new URLSearchParams(window.location.search);
    return parseItems(params.get('items') || config.defaultItems || DEFAULT_ITEMS);
  };

  const fetchCart = () => fetch('/cart.js')
    .then((res) => (res.ok ? res.json() : { items: [] }))
    .catch(() => ({ items: [] }));

  const renderPreCart = (root, items, productMap) => {
    const currentEl = root.querySelector('[data-upsell-current]');
    const offersEl = root.querySelector('[data-upsell-offers]');
    const skipEl = root.querySelector('[data-upsell-skip]');
    const plans = planDefinitions(items);

    if (currentEl) {
      currentEl.innerHTML = `
        <div class="upsell-test__panel upsell-test__panel--soft">
          <p class="upsell-test__eyebrow">Current recommendation</p>
          <h2 class="upsell-test__section-title">現在の1商品提案</h2>
          ${renderItemList(items.slice(0, 1), productMap)}
          <p class="upsell-test__note">この画面では、1商品購入を残しながら2/3商品セットへのアップグレードを比較できます。</p>
        </div>`;
    }

    if (offersEl) offersEl.innerHTML = renderOfferCards(plans, productMap, 'precart');
    if (skipEl) skipEl.setAttribute('href', cartAddUrl(items.slice(0, 1), '/cart'));
  };

  const renderPreCheckout = (root, items, productMap, cart) => {
    const currentEl = root.querySelector('[data-upsell-current]');
    const offersEl = root.querySelector('[data-upsell-offers]');
    const skipEl = root.querySelector('[data-upsell-skip]');
    const cartVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id)));
    let recommendations = items.filter((item) => !cartVariantIds.has(String(item.id)));
    if (recommendations.length === 0) recommendations = items.slice(1, 3);
    if (recommendations.length === 0) recommendations = items.slice(0, 2);

    const cartItems = (cart.items || []).slice(0, 4).map((line) => ({
      id: String(line.variant_id),
      qty: line.quantity,
      title: line.product_title || line.title,
      variantTitle: line.variant_title || '',
      price: line.final_price ? line.final_price / 100 : null,
      currencyCode: 'JPY',
      imageUrl: line.image || '',
      imageAlt: line.product_title || line.title,
      description: line.variant_title || 'カート投入済み商品'
    }));
    const cartMap = new Map(cartItems.map((item) => [item.id, item]));

    if (currentEl) {
      currentEl.innerHTML = `
        <div class="upsell-test__panel upsell-test__panel--soft">
          <p class="upsell-test__eyebrow">Cart snapshot</p>
          <h2 class="upsell-test__section-title">現在のカート内容</h2>
          ${cartItems.length ? renderItemList(cartItems, cartMap) : '<p class="upsell-test__note">カートが空の場合は、サンプル商品で決済前アップセルを表示します。</p>'}
          <p class="upsell-test__note">カートのチェックアウトボタンからこの画面を挟み、追加/スキップ後に決済へ進む検証です。</p>
        </div>`;
    }

    const plans = [
      {
        key: 'one-more',
        title: 'おすすめを1点追加',
        badge: '決済前提案',
        copy: '決済直前に1商品だけ追加提案する、摩擦の少ないパターンです。',
        items: recommendations.slice(0, 1),
        featured: true
      },
      {
        key: 'two-more',
        title: 'おすすめを2点追加',
        badge: '単価最大化',
        copy: '決済直前に関連度の高い2商品をまとめて提案するパターンです。',
        items: recommendations.slice(0, 2)
      }
    ].filter((plan) => plan.items.length > 0);

    if (offersEl) offersEl.innerHTML = renderOfferCards(plans, productMap, 'precheckout');
    if (skipEl) skipEl.setAttribute('href', '/checkout');
  };

  const renderThankYou = (root, items, productMap) => {
    const offersEl = root.querySelector('[data-upsell-offers]');
    if (!offersEl) return;
    const plans = [
      {
        key: 'after-purchase',
        title: '次回におすすめのサプリ',
        badge: 'Thank you表示',
        copy: '購入完了後に軽い文章と商品カードを出し、次回購入や追加購入の検討につなげます。',
        items: items.slice(0, 2),
        featured: true
      }
    ].filter((plan) => plan.items.length > 0);
    offersEl.innerHTML = renderOfferCards(plans, productMap, 'thank-you');
  };

  const initOfferPages = () => {
    document.querySelectorAll('[data-upsell-test-root]').forEach((root) => {
      if (root.dataset.upsellReady === 'true') return;
      root.dataset.upsellReady = 'true';
      const mode = root.dataset.upsellMode || 'precart';
      const config = getConfig(root);
      const items = getPageItems(root, config);
      const stateEl = root.querySelector('[data-upsell-state]');

      if (!items.length) {
        if (stateEl) stateEl.textContent = '表示する商品がありません。';
        return;
      }

      if (stateEl) stateEl.textContent = '商品情報を取得中です...';
      fetchVariants(items.map((item) => item.id), config).then((productMap) => {
        if (mode === 'precheckout') {
          fetchCart().then((cart) => renderPreCheckout(root, items, productMap, cart));
        } else if (mode === 'thank-you') {
          renderThankYou(root, items, productMap);
        } else {
          renderPreCart(root, items, productMap);
        }
        if (stateEl) stateEl.hidden = true;
      });
    });
  };

  const initCartWidgets = () => {
    document.querySelectorAll('[data-upsell-cart-widget]').forEach((widget) => {
      if (widget.dataset.upsellReady === 'true') return;
      widget.dataset.upsellReady = 'true';
      const config = getConfig(widget);
      const holder = widget.querySelector('[data-upsell-cart-items]');
      const stateEl = widget.querySelector('[data-upsell-cart-state]');
      if (!holder) return;

      Promise.all([
        fetchCart(),
        fetchVariants(parseItems(config.defaultItems).map((item) => item.id), config)
      ]).then(([cart, productMap]) => {
        const cartVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id)));
        let recommendations = parseItems(config.defaultItems).filter((item) => !cartVariantIds.has(String(item.id))).slice(0, 2);
        if (recommendations.length === 0) recommendations = parseItems(config.defaultItems).slice(0, 2);

        holder.innerHTML = recommendations.map((item) => {
          const product = productMap.get(item.id) || fallbackProduct(item.id);
          const image = product.imageUrl
            ? `<img class="upsell-test-cart__image" src="${escapeHtml(product.imageUrl)}" alt="${escapeHtml(product.imageAlt || displayName(product))}" loading="lazy">`
            : '<span class="upsell-test-cart__image" aria-hidden="true"></span>';
          return `<li class="upsell-test-cart__item">
            ${image}
            <div>
              <p class="upsell-test-cart__title">${escapeHtml(displayName(product))}</p>
              <p class="upsell-test-cart__meta">${escapeHtml(totalFor([item], productMap))}</p>
            </div>
            <a class="upsell-test-cart__add" href="${cartAddUrl([item], '/cart')}">追加</a>
          </li>`;
        }).join('');
        if (stateEl) stateEl.hidden = true;
      });
    });
  };

  const initPreCheckoutRedirect = () => {
    if (preCheckoutRedirectBound) return;
    preCheckoutRedirectBound = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element
        ? event.target.closest('[data-upsell-precheckout-trigger]')
        : null;
      if (!trigger) return;
      const target = trigger.getAttribute('data-upsell-precheckout-url') || '/pages/upsell-precheckout';
      event.preventDefault();
      window.location.assign(target);
    }, true);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initOfferPages();
    initCartWidgets();
    initPreCheckoutRedirect();
  });

  initPreCheckoutRedirect();
})();
